<script setup lang="ts">
import { computed, ref } from 'vue';
import ButtonGroup from 'primevue/buttongroup'; // NOTE :: The PrimeVue resolver does not auto-import ButtonGroup
import { useI18n } from 'vue-i18n';
import type { SavedReport } from '../../../interfaces';
import type { ApiResponse } from '@Types';
import { useADialog, useAToast } from '@Atoms';
import { useSavedReports } from '../../../composables';
import { DeleteSavedReportService, SetDefaultSavedReportService, SetSharedSavedReportService } from '../../../services';

const emit = defineEmits<{
  onApplySavedReport: [];
}>();

const props = defineProps<{
  dataTableId: string;
  loading?: boolean;
}>();

const { t } = useI18n();
const { activeModal, activeModalProps, dialogVisibility, isCurrentModalOpen, reset, SYSTEM_MODALS } = useADialog();
const { notify, TOAST_TYPES } = useAToast();
const {
  activeSavedReport,
  applySavedReport,
  clearSavedReport,
  getListSavedReports,
  hasChanges,
  isReportManager,
  isSavedReportGoneError,
  isSavedReportModal,
  isSavedReportsOffError,
  isStandardView,
  removeSavedReport,
  selectSavedReport,
  upsertSavedReport,
} = useSavedReports();

const TOAST_DEFAULT_REMOVED = { title: 'filters.savedReports.defaultRemovedToast', type: TOAST_TYPES.SUCCESS };
const TOAST_DEFAULT_SET = { title: 'filters.savedReports.defaultSetToast', type: TOAST_TYPES.SUCCESS };
const TOAST_DELETED = { title: 'filters.savedReports.deletedToast', type: TOAST_TYPES.SUCCESS };
const TOAST_ERROR = { title: 'filters.savedReports.errorToast', type: TOAST_TYPES.ERROR };
const TOAST_FEATURE_OFF = { title: 'filters.savedReports.featureOffToast', type: TOAST_TYPES.WARNING };
const TOAST_GONE = { title: 'filters.savedReports.goneToast', type: TOAST_TYPES.WARNING };
const TOAST_SHARED = { title: 'filters.savedReports.sharedToast', type: TOAST_TYPES.SUCCESS };
const TOAST_STALE_SKIPPED = { title: 'filters.savedReports.staleSkippedToast', type: TOAST_TYPES.INFO };
const TOAST_UNSHARE_CONFIRM = { title: 'filters.savedReports.unshareConfirmToast', type: TOAST_TYPES.WARNING };
const TOAST_UNSHARED = { title: 'filters.savedReports.unsharedToast', type: TOAST_TYPES.SUCCESS };

const confirmingUnshareId = ref('');
const isRequesting = ref(false);

const hasReportChanges = computed(() => hasChanges());
const isCompact = computed(() => !activeSavedReport.value);
const isTemplatePreview = computed(() => !!activeSavedReport.value && !activeSavedReport.value.is_owner);
const isDialogOpen = computed(() => isSavedReportModal(activeModal.value));
const isStandard = computed(() => isStandardView());
const reports = computed(() => getListSavedReports(props.dataTableId));

// NOTE :: One control, five looks. No report open: a bookmark icon that opens the list, plus a save button once
// the view differs from the plain list. A report of mine open: its name (amber bookmark) plus a red X that closes it
// (the trash icon is kept for a real delete); once I changed it, the teal update (or save) button sits before the
// red X, so I can still drop the changes and go back to the plain list. A template opened from the Shared Reports
// page ("View filter"): a teal save button that keeps it as my own report, next to the red X.
const actionButton = computed(() => {
  if (!activeSavedReport.value) {
    return isStandard.value
      ? null
      : {
          classes: 'bg-white text-brand-500',
          icon: 'pi pi-save',
          tooltip: 'filters.savedReports.saveTooltip',
          onClick: () => openSaveDialog(false),
        };
  }

  if (isTemplatePreview.value) {
    return {
      classes: 'border-brand-500 bg-brand-500 text-white',
      icon: 'pi pi-save',
      tooltip: 'filters.savedReports.saveCopyTooltip',
      onClick: () => openSaveDialog(true, activeSavedReport.value?._id),
    };
  }

  // NOTE :: A shared report reaches everyone, so only a report manager updates it; anyone else saves the change as
  // a new report of their own (the api refuses the update the same way).
  if (hasReportChanges.value && activeSavedReport.value.shareable && !isReportManager.value) {
    return {
      classes: 'border-brand-500 bg-brand-500 text-white',
      icon: 'pi pi-save',
      tooltip: 'filters.savedReports.sharedNotManagerTooltip',
      onClick: () => openSaveDialog(true),
    };
  }

  if (hasReportChanges.value) {
    return {
      classes: 'border-brand-500 bg-brand-500 text-white',
      icon: 'pi pi-refresh',
      tooltip: 'filters.savedReports.updateTooltip',
      onClick: openUpdateDialog,
    };
  }

  return {
    classes: 'bg-white text-red-500',
    icon: 'pi pi-times',
    tooltip: 'filters.savedReports.clearTooltip',
    onClick: onClearReport,
  };
});
const actionButtonTooltip = computed(() => ({
  value: actionButton.value ? t(actionButton.value.tooltip) : '',
  showDelay: 1000,
}));
// NOTE :: The red X that sits after the teal button: on a preview, and on a report of mine with unsaved changes.
const hasSeparateCloseButton = computed(
  () => isTemplatePreview.value || (!!activeSavedReport.value && hasReportChanges.value)
);
const triggerClasses = computed(() => [
  isCompact.value ? 'w-9' : 'w-[170px]',
  actionButton.value ? 'rounded-l-md rounded-r-none border-r-0' : 'rounded-md',
]);
const triggerInputClasses = computed(() =>
  isCompact.value
    ? 'flex w-9 items-center justify-center p-0 text-gray-500'
    : 'flex min-w-0 items-center p-1 pl-3 text-sm text-slate-700'
);
const triggerTooltip = computed(() => ({
  value: isCompact.value ? t('filters.savedReports.label') : '',
  showDelay: 1000,
}));

// HELPERS
function getDefaultTooltip(report: SavedReport) {
  return report.is_default ? 'filters.savedReports.removeDefaultTooltip' : 'filters.savedReports.setDefaultTooltip';
}

// NOTE :: A report manager shares and unshares their reports; anyone else only sees the icon on a report of theirs
// that is still shared, to stop sharing it.
function canToggleShare(report: SavedReport) {
  return isReportManager.value || report.shareable;
}

function getShareIconClass(report: SavedReport) {
  if (confirmingUnshareId.value === report._id) return 'text-red-500';

  return report.shareable ? 'text-brand-500' : 'text-gray-400';
}

function getShareTooltip(report: SavedReport) {
  return report.shareable ? 'filters.savedReports.unshareTooltip' : 'filters.savedReports.shareTooltip';
}

function openSaveDialog(isSaveAsNew: boolean, sourceTemplateId?: string) {
  activeModalProps.value = { isSaveAsNew, sourceTemplateId };
  activeModal.value = SYSTEM_MODALS.SAVED_REPORT_SAVE;
  dialogVisibility.value = true;
}

function openUpdateDialog() {
  activeModalProps.value = { savedReport: activeSavedReport.value };
  activeModal.value = SYSTEM_MODALS.SAVED_REPORT_UPDATE;
  dialogVisibility.value = true;
}

function openDeleteDialog(report: SavedReport) {
  activeModalProps.value = { rowInfo: report, toBeDeleted: report.name };
  activeModal.value = SYSTEM_MODALS.SAVED_REPORT_DELETE;
  dialogVisibility.value = true;
}

// EVENTS
// NOTE :: Closing the list drops a pending "click again to stop sharing", so a later click asks again.
function onDropdownHide() {
  confirmingUnshareId.value = '';
}

function onReportSelect(savedReportId: string | null) {
  const report = reports.value.find(({ _id }) => _id === savedReportId);
  if (!report) return onClearReport();

  const hasSkippedSettings = applySavedReport(report);
  if (hasSkippedSettings) notify(TOAST_STALE_SKIPPED);

  emit('onApplySavedReport');
}

function onClearReport() {
  clearSavedReport();

  if (isDialogOpen.value) reset();

  emit('onApplySavedReport');
}

function onReportSaved(report: SavedReport) {
  upsertSavedReport(report);
  selectSavedReport(report);
}

function onToggleDefault(report: SavedReport) {
  if (isRequesting.value) return;

  isRequesting.value = true;

  const PAYLOAD = { _id: report._id, is_default: !report.is_default };

  SetDefaultSavedReportService(PAYLOAD)
    .then(onDefaultToggled)
    .catch((error) => onRequestError(error, report))
    .finally(() => (isRequesting.value = false));
}

function onDefaultToggled({ data }: ApiResponse<SavedReport>) {
  upsertSavedReport(data);

  notify(data.is_default ? TOAST_DEFAULT_SET : TOAST_DEFAULT_REMOVED);
}

// NOTE :: Someone who is not a report manager can stop sharing their own report but can't share it again, so their
// stop asks twice: the first click warns and turns the icon red, the second one stops it.
function onToggleShared(report: SavedReport) {
  if (isRequesting.value) return;

  const needsConfirm = report.shareable && !isReportManager.value && confirmingUnshareId.value !== report._id;
  if (needsConfirm) {
    confirmingUnshareId.value = report._id;

    return notify(TOAST_UNSHARE_CONFIRM);
  }

  confirmingUnshareId.value = '';
  isRequesting.value = true;

  const PAYLOAD = { _id: report._id, shareable: !report.shareable };

  SetSharedSavedReportService(PAYLOAD)
    .then(onSharedToggled)
    .catch((error) => onRequestError(error, report))
    .finally(() => (isRequesting.value = false));
}

function onSharedToggled({ data }: ApiResponse<SavedReport>) {
  upsertSavedReport(data);

  notify(data.shareable ? TOAST_SHARED : TOAST_UNSHARED);
}

function onDeleteReport(report: SavedReport) {
  if (isRequesting.value) return;

  isRequesting.value = true;

  DeleteSavedReportService(report._id)
    .then(() => onReportDeleted(report))
    .catch((error) => onRequestError(error, report))
    .finally(() => (isRequesting.value = false));
}

function onReportDeleted(report: SavedReport) {
  const wasOpen = activeSavedReport.value?._id === report._id;

  removeSavedReport(report._id);

  notify(TOAST_DELETED);

  reset();

  if (!wasOpen) return;

  clearSavedReport();

  emit('onApplySavedReport');
}

// NOTE :: Sharing past the shelter's cap answers `saved_reports_shared_limit` with the cap itself, so the message
// names the real number.
function onRequestError(error: any, report: SavedReport) {
  if (isSavedReportGoneError(error)) return onReportGone(report);

  const responseData = error?.response?.data;
  const isSharedLimitReached = responseData?.code === 'saved_reports_shared_limit';
  if (isSharedLimitReached) {
    const TOAST_SHARED_LIMIT = {
      title: 'filters.savedReports.sharedLimitReachedTitle',
      message: t('filters.savedReports.sharedLimitReachedMessage', { limit: responseData.limit }),
      type: TOAST_TYPES.WARNING,
    };

    return notify(TOAST_SHARED_LIMIT);
  }

  notify(isSavedReportsOffError(error) ? TOAST_FEATURE_OFF : TOAST_ERROR);
}

// NOTE :: Deleted in another tab or device since this list loaded: drop the dead row, and close it if open.
function onReportGone(report: SavedReport) {
  const wasOpen = activeSavedReport.value?._id === report._id;

  removeSavedReport(report._id);

  notify(TOAST_GONE);

  if (isDialogOpen.value) reset();

  if (!wasOpen) return;

  clearSavedReport();

  emit('onApplySavedReport');
}
</script>

<template>
  <!-- NOTE :: One control serves every list with saved reports, so the New bubble shows on each of them; hiding it
       on one list hides it everywhere. -->
  <MHelpBubble tipKey="saved-reports">
    <ButtonGroup class="flex flex-nowrap">
      <!-- NOTE :: The tooltip sits on the FloatLabel, not the Dropdown. PrimeVue 3 Tooltip aims at the <input> inside
           a `.p-inputwrapper`, and this Dropdown has none, so on the Dropdown it throws on every update and the list's
           clicks stop working (the Filters dialog would not open). -->
      <FloatLabel
        v-tooltip.top="triggerTooltip"
        :class="['transition-[margin] duration-200', { '-mr-1': actionButton }]"
      >
        <Dropdown
          filter
          inputId="savedReports"
          optionLabel="name"
          optionValue="_id"
          pt:emptymessage="text-sm"
          pt:filterInput="h-9 py-1 pl-3 text-sm"
          pt:item="p-0 text-sm"
          pt:panel="w-[280px]"
          :ariaLabel="$t('filters.savedReports.label')"
          :class="['flex h-9 gap-3 !border-gray-300 transition-all duration-200', triggerClasses]"
          :disabled="loading || isRequesting"
          :emptyFilterMessage="$t('filters.savedReports.notFound')"
          :emptyMessage="$t('filters.savedReports.empty')"
          :modelValue="activeSavedReport?._id ?? null"
          :options="reports"
          :pt:input="triggerInputClasses"
          :pt:trigger="isCompact ? 'hidden' : 'w-8'"
          @hide="onDropdownHide"
          @update:modelValue="onReportSelect"
        >
          <template #value>
            <i
              v-if="isCompact"
              class="pi pi-bookmark text-sm"
            />

            <span
              v-else
              class="truncate"
            >
              {{ activeSavedReport?.name }}
            </span>
          </template>

          <template #option="{ option }">
            <div class="flex w-full items-center gap-1 py-1.5 pl-3 pr-1">
              <span
                class="min-w-0 flex-1 truncate"
                :title="option.name"
              >
                {{ option.name }}
              </span>

              <Button
                v-tooltip.top="$t(getDefaultTooltip(option))"
                rounded
                text
                :aria-label="$t(getDefaultTooltip(option))"
                :class="['size-7 p-0', option.is_default ? 'text-warning-400' : 'text-gray-400']"
                :disabled="isRequesting"
                :icon="option.is_default ? 'pi pi-star-fill' : 'pi pi-star'"
                @click.stop="onToggleDefault(option)"
              />

              <Button
                v-if="canToggleShare(option)"
                v-tooltip.top="$t(getShareTooltip(option))"
                rounded
                text
                icon="pi pi-users"
                :aria-label="$t(getShareTooltip(option))"
                :class="['size-7 p-0', getShareIconClass(option)]"
                :disabled="isRequesting"
                @click.stop="onToggleShared(option)"
              />

              <Button
                v-tooltip.top="$t('filters.savedReports.deleteTooltip')"
                rounded
                text
                class="size-7 p-0 text-red-500"
                icon="pi pi-trash"
                :aria-label="$t('filters.savedReports.deleteTooltip')"
                :disabled="isRequesting"
                @click.stop="openDeleteDialog(option)"
              />
            </div>
          </template>
        </Dropdown>

        <Transition
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="translate-y-1 opacity-0"
          leave-active-class="transition duration-150 ease-in"
          leave-to-class="translate-y-1 opacity-0"
        >
          <label
            v-if="!isCompact"
            class="-mt-2.5 flex items-center gap-1 whitespace-nowrap text-sm text-zinc-500"
            for="savedReports"
          >
            <i class="pi pi-bookmark-fill text-sm text-warning-400" />

            <span>{{ $t('filters.savedReports.label') }}</span>
          </label>
        </Transition>
      </FloatLabel>

      <Transition
        enter-active-class="overflow-hidden transition-all duration-200 ease-out"
        enter-from-class="max-w-0 opacity-0"
        enter-to-class="max-w-12 opacity-100"
        leave-active-class="overflow-hidden transition-all duration-150 ease-in"
        leave-from-class="max-w-12 opacity-100"
        leave-to-class="max-w-0 opacity-0"
        mode="out-in"
      >
        <Button
          v-if="actionButton"
          v-tooltip.top="actionButtonTooltip"
          :key="actionButton.icon"
          pt:root="h-9 rounded-l-none border-[1.5px] border-gray-300 px-4 py-1.5 text-sm transition-colors duration-200"
          severity="secondary"
          :aria-label="$t(actionButton.tooltip)"
          :class="[actionButton.classes, hasSeparateCloseButton ? 'rounded-r-none' : 'rounded-r-md']"
          :disabled="loading || isRequesting"
          :icon="actionButton.icon"
          @click="actionButton.onClick"
        />
      </Transition>

      <Button
        v-if="hasSeparateCloseButton"
        v-tooltip.top="{ value: $t('filters.savedReports.clearTooltip'), showDelay: 1000 }"
        icon="pi pi-times"
        pt:root="h-9 rounded-l-none rounded-r-md border-[1.5px] border-l-0 border-gray-300 bg-white px-4 py-1.5 text-sm text-red-500"
        severity="secondary"
        :aria-label="$t('filters.savedReports.clearTooltip')"
        :disabled="loading || isRequesting"
        @click="onClearReport"
      />
    </ButtonGroup>
  </MHelpBubble>

  <ADialog v-if="isDialogOpen">
    <MSaveReportDialog
      v-if="isCurrentModalOpen(SYSTEM_MODALS.SAVED_REPORT_SAVE)"
      v-bind="activeModalProps"
      :dataTableId
      @onSaved="onReportSaved"
    />

    <MUpdateReportDialog
      v-else-if="isCurrentModalOpen(SYSTEM_MODALS.SAVED_REPORT_UPDATE)"
      v-bind="activeModalProps"
      @onClear="onClearReport"
      @onSaveAsNew="openSaveDialog(true)"
      @onUpdated="onReportSaved"
    />

    <MDeleteModal
      v-else-if="isCurrentModalOpen(SYSTEM_MODALS.SAVED_REPORT_DELETE)"
      v-bind="activeModalProps"
      :loading="isRequesting"
      @onDelete="onDeleteReport"
    />
  </ADialog>
</template>
